import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {CartesetLink} from './cartesetLinks';

type Curve = {
    key: string;
    d: string;
    fromKey: string;
    toKey: string;
    amount: number;
    explicit: boolean;
    labelX: number;
    labelY: number;
    anchor: 'start' | 'end';
};

type Rect = { x: number; y: number; w: number; h: number };
type Box = { left: number; right: number; top: number; bottom: number; width: number; height: number };

let overlaySeq = 0;

const escapeAttr = (s: string) =>
    (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(s) : String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

function cellOf(wrap: HTMLElement, rowId: string, colId: string): HTMLElement | null {
    return wrap.querySelector(`.ag-row[row-id="${escapeAttr(rowId)}"] .ag-cell[col-id="${escapeAttr(colId)}"]`);
}

function colSample(wrap: HTMLElement, colId: string): HTMLElement | null {
    return wrap.querySelector(`.ag-body-viewport .ag-cell[col-id="${escapeAttr(colId)}"]`)
        || wrap.querySelector(`.ag-cell[col-id="${escapeAttr(colId)}"]`);
}

/** The painted ₪ amount inside a right-aligned AG Grid cell, else the cell itself. */
function amountBox(cell: HTMLElement): Box {
    const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    let last: Text | null = null;
    while ((node = walker.nextNode())) {
        if (node.textContent && node.textContent.trim()) last = node as Text;
    }
    if (last) {
        const range = document.createRange();
        range.selectNodeContents(last);
        const rects = range.getClientRects();
        if (rects.length) {
            const r = rects[rects.length - 1];
            if (r.width > 2 && r.height > 2) {
                return {left: r.left, right: r.right, top: r.top, bottom: r.bottom, width: r.width, height: r.height};
            }
        }
    }
    const r = cell.getBoundingClientRect();
    return {left: r.left, right: r.right, top: r.top, bottom: r.bottom, width: r.width, height: r.height};
}

const GRID_EVENTS = [
    'bodyScroll',
    'modelUpdated',
    'rowDataUpdated',
    'firstDataRendered',
    'displayedColumnsChanged',
    'virtualColumnsChanged',
    'columnResized',
    'columnMoved',
    'columnPinned',
    'gridSizeChanged',
    'rowGroupOpened',
    'sortChanged',
    'filterChanged',
];

/**
 * Draws light green זכות → חובה curves on top of an AG Grid carteset.
 * Each curve leaves the ₪ amount in the from-column (זכות) and lands on the
 * ₪ amount in the to-column (חובה), same as the collection carteset.
 * Off-screen rows still use the grid row model (Y) and the column boxes (X)
 * and are clipped to the viewport.
 */
export default function CartesetLinksOverlay({
                                                 gridRef,
                                                 links,
                                                 rowKey,
                                                 fromColId,
                                                 toColId,
                                                 hidden,
                                                 children,
                                             }: {
    gridRef: React.MutableRefObject<any>;
    links: CartesetLink[];
    rowKey: (row: any) => string;
    fromColId: string;
    toColId: string;
    hidden?: boolean;
    children: React.ReactNode;
}) {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [curves, setCurves] = useState<Curve[]>([]);
    const [clip, setClip] = useState<Rect | null>(null);
    const [hoverKey, setHoverKey] = useState<string | null>(null);
    const idsRef = useRef<{ marker: string; clip: string } | null>(null);
    if (!idsRef.current) {
        overlaySeq += 1;
        idsRef.current = {marker: `carteset-link-arrow-${overlaySeq}`, clip: `carteset-link-clip-${overlaySeq}`};
    }
    const ids = idsRef.current;

    const linksRef = useRef(links);
    linksRef.current = links;
    const hiddenRef = useRef(!!hidden);
    hiddenRef.current = !!hidden;

    const relayout = useCallback(() => {
        const wrap = wrapRef.current;
        const api = gridRef.current?.api;
        if (!wrap || !api || hiddenRef.current || !linksRef.current.length) {
            setCurves((prev) => (prev.length ? [] : prev));
            return;
        }
        if (typeof api.isDestroyed === 'function' && api.isDestroyed()) return;

        const viewport = wrap.querySelector('.ag-body-viewport') as HTMLElement | null;
        const container = wrap.querySelector('.ag-center-cols-container') as HTMLElement | null;
        const fromCell = colSample(wrap, fromColId);
        const toCell = colSample(wrap, toColId);
        if (!viewport || !container || !fromCell || !toCell) {
            setCurves((prev) => (prev.length ? [] : prev));
            return;
        }

        const wr = wrap.getBoundingClientRect();
        const vr = viewport.getBoundingClientRect();
        const cr = container.getBoundingClientRect();
        const fromCol = fromCell.getBoundingClientRect();
        const toCol = toCell.getBoundingClientRect();
        setClip({x: vr.left - wr.left, y: vr.top - wr.top, w: vr.width, h: vr.height});

        // RTL carteset: חובה sits to the right of זכות (mirrored for suppliers).
        const toIsRight = toCol.left >= fromCol.left;
        const dir = toIsRight ? 1 : -1;

        const minY = vr.top - wr.top - 4;
        const maxY = vr.bottom - wr.top + 4;
        const next: Curve[] = [];
        linksRef.current.forEach((l, n) => {
            const fk = rowKey(l.from);
            const tk = rowKey(l.to);
            const a = api.getRowNode(fk);
            const b = api.getRowNode(tk);
            if (!a || !b) return;
            if (a.rowTop == null || b.rowTop == null) return;
            if (a.displayed === false || b.displayed === false) return;

            const fromEl = cellOf(wrap, fk, fromColId);
            const toEl = cellOf(wrap, tk, toColId);
            const fromBox = fromEl ? amountBox(fromEl) : null;
            const toBox = toEl ? amountBox(toEl) : null;

            const y1 = fromBox
                ? fromBox.top - wr.top + fromBox.height / 2
                : cr.top - wr.top + a.rowTop + (a.rowHeight || 0) / 2;
            const y2 = toBox
                ? toBox.top - wr.top + toBox.height / 2
                : cr.top - wr.top + b.rowTop + (b.rowHeight || 0) / 2;
            if (Math.abs(y1 - y2) < 2) return;
            if ((y1 < minY && y2 < minY) || (y1 > maxY && y2 > maxY)) return;

            // Leave the זכות number on the side facing חובה; land on the חובה
            // number from the facing side — not a squiggle on the shared border.
            const x1 = fromBox
                ? (toIsRight ? fromBox.right : fromBox.left) - wr.left + dir * 2
                : (toIsRight ? fromCol.right : fromCol.left) - wr.left - dir * 6;
            const x2 = toBox
                ? (toIsRight ? toBox.left : toBox.right) - wr.left - dir * 3
                : (toIsRight ? toCol.left : toCol.right) - wr.left + dir * Math.max(16, toCol.width * 0.32);

            const mid = (x1 + x2) / 2;
            const stagger = ((n % 5) - 2) * 6;
            let cx = mid + stagger;
            if (Math.abs(x2 - x1) < 18) {
                cx = mid + dir * (20 + Math.abs(stagger));
            }
            next.push({
                key: `${fk}->${tk}`,
                d: `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`,
                fromKey: fk,
                toKey: tk,
                amount: l.amount,
                explicit: l.source === 'explicit',
                labelX: cx + dir * 6,
                labelY: (y1 + y2) / 2,
                anchor: dir > 0 ? 'start' : 'end',
            });
        });
        setCurves(next);
    }, [gridRef, rowKey, fromColId, toColId]);

    const rafRef = useRef<number | null>(null);
    const schedule = useCallback(() => {
        if (rafRef.current != null) return;
        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            relayout();
        });
    }, [relayout]);

    // Row animations finish a few hundred ms after model events.
    const scheduleSettled = useCallback(() => {
        schedule();
        window.setTimeout(schedule, 250);
        window.setTimeout(schedule, 550);
    }, [schedule]);

    useLayoutEffect(() => {
        scheduleSettled();
    }, [links, hidden, scheduleSettled]);

    // The grid mounts asynchronously inside AgTable; poll until its api exists,
    // then subscribe to everything that can move a row or a column.
    useEffect(() => {
        let api: any = null;
        let timer: number | null = null;
        const onHoverIn = (e: any) => setHoverKey(e?.node?.id ?? null);
        const onHoverOut = () => setHoverKey(null);

        const attach = () => {
            const cand = gridRef.current?.api;
            if (!cand) return false;
            api = cand;
            GRID_EVENTS.forEach((ev) => api.addEventListener(ev, scheduleSettled));
            api.addEventListener('cellMouseOver', onHoverIn);
            api.addEventListener('cellMouseOut', onHoverOut);
            scheduleSettled();
            return true;
        };
        if (!attach()) {
            timer = window.setInterval(() => {
                if (attach() && timer != null) {
                    window.clearInterval(timer);
                    timer = null;
                }
            }, 300);
        }

        const ro = typeof ResizeObserver !== 'undefined' && wrapRef.current
            ? new ResizeObserver(() => schedule())
            : null;
        if (ro && wrapRef.current) ro.observe(wrapRef.current);
        window.addEventListener('resize', schedule);

        return () => {
            if (timer != null) window.clearInterval(timer);
            if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
            window.removeEventListener('resize', schedule);
            ro?.disconnect();
            if (api && !(typeof api.isDestroyed === 'function' && api.isDestroyed())) {
                try {
                    GRID_EVENTS.forEach((ev) => api.removeEventListener(ev, scheduleSettled));
                    api.removeEventListener('cellMouseOver', onHoverIn);
                    api.removeEventListener('cellMouseOut', onHoverOut);
                } catch {
                    // grid already torn down
                }
            }
        };
    }, [gridRef, schedule, scheduleSettled]);

    const show = !hidden && curves.length > 0 && !!clip;

    return (
        <div ref={wrapRef} style={{position: 'relative'}}>
            {children}
            {show ? (
                <svg
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        overflow: 'visible',
                        zIndex: 3,
                    }}
                >
                    <defs>
                        <marker
                            id={ids.marker}
                            markerWidth="7"
                            markerHeight="7"
                            refX="5.5"
                            refY="3.5"
                            orient="auto"
                            markerUnits="userSpaceOnUse"
                        >
                            <path d="M0.5,0.5 L6.5,3.5 L0.5,6.5 Z" fill="#2e7d32"/>
                        </marker>
                        <clipPath id={ids.clip}>
                            <rect x={clip!.x} y={clip!.y} width={clip!.w} height={clip!.h}/>
                        </clipPath>
                    </defs>
                    <g clipPath={`url(#${ids.clip})`}>
                        {curves.map((c) => {
                            const hot = hoverKey != null && (c.fromKey === hoverKey || c.toKey === hoverKey);
                            const dim = hoverKey != null && !hot;
                            return (
                                <g key={c.key}>
                                    <path
                                        d={c.d}
                                        fill="none"
                                        stroke="#2e7d32"
                                        strokeWidth={hot ? 2.2 : 1.3}
                                        strokeOpacity={hot ? 0.95 : dim ? 0.1 : 0.38}
                                        strokeDasharray={c.explicit ? undefined : '4 3'}
                                        strokeLinecap="round"
                                        markerEnd={`url(#${ids.marker})`}
                                        style={{transition: 'stroke-opacity 120ms, stroke-width 120ms'}}
                                    />
                                    {hot ? (
                                        <text
                                            x={c.labelX}
                                            y={c.labelY}
                                            fontSize="11"
                                            fontWeight={600}
                                            fill="#1b5e20"
                                            textAnchor={c.anchor}
                                            dominantBaseline="middle"
                                            style={{paintOrder: 'stroke', stroke: '#fff', strokeWidth: 3}}
                                        >
                                            ₪ {c.amount.toLocaleString()}
                                        </text>
                                    ) : null}
                                </g>
                            );
                        })}
                    </g>
                </svg>
            ) : null}
        </div>
    );
}
